import http from "./http.service";
import Promisable from "./promisable.service";
import { NavigateFunction } from "react-router-dom";
import { getAppDispatch } from "utils/dispatch.util";
import { modalActions } from "redux/slices/modal";
import { specialPriceActions } from "redux/slices/specialPrice";
import { formLoaderActions } from "redux/slices/formLoader";

const url = "/special-price";

const SpecialPriceService = {
  /** One submit covers many zones x classes, so the API takes the whole batch. */
  addSpecialPrices: async (data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}`, data)
    );

    if (success) navigate?.("/special-prices");

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },

  getAllSpecialPrices: async (data?: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(specialPriceActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/all`, data || {})
    );

    if (success) {
      const { specialPrices, count } = success.data.data;
      dispatch?.(specialPriceActions.setSpecialPrices({ specialPrices, count }));
    }

    dispatch?.(specialPriceActions.setLoading(false));
    return [success, error];
  },

  getSpecialPrice: async (id: string) => {
    const dispatch = getAppDispatch();
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/${id}`)
    );

    if (success) {
      const { specialPrice } = success.data.data;
      dispatch?.(specialPriceActions.setSpecialPrice(specialPrice));
    }

    return [success, error];
  },

  updateSpecialPrice: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.patch(`${url}/${id}`, data)
    );

    if (success) {
      const { specialPrice } = success.data.data;
      dispatch?.(specialPriceActions.updateSpecialPrice({ id, specialPrice }));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  /**
   * Saves the periods a class-level edit actually changed. The API has no bulk
   * PATCH, so this walks them one at a time.
   *
   * Sequential, not parallel, and deliberately so: the API rejects an update
   * that would overlap another window of the same class, and it evaluates that
   * against whatever is stored at the moment the request lands. Firing them
   * together makes which one wins a race. Walking them in order at least makes
   * the outcome repeatable and lets the caller say exactly which row was
   * refused.
   *
   * Returns the ids that failed — empty means everything saved.
   */
  updateManySpecialPrices: async (
    updates: { id: string; data: any }[]
  ): Promise<string[]> => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const failed: string[] = [];

    for (const { id, data } of updates) {
      const [success]: any = await Promisable.asPromise(
        // Silenced individually; the caller reports the batch once.
        http.patch(`${url}/${id}`, data, { silentToast: true } as any)
      );

      if (success) {
        const { specialPrice } = success.data.data;
        dispatch?.(specialPriceActions.updateSpecialPrice({ id, specialPrice }));
      } else {
        failed.push(id);
      }
    }

    // Kept open on any failure so the refused rows stay on screen next to the
    // error explaining them, with the user's edits intact.
    if (!failed.length) dispatch?.(modalActions.closeModal());

    dispatch?.(modalActions.setLoading(false));
    return failed;
  },

  deleteSpecialPrice: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.delete(`${url}/${id}`)
    );

    if (success) {
      dispatch?.(specialPriceActions.deleteSpecialPrice({ id }));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  /** Bulk delete from the list's selection — one request, one confirmation. */
  deleteManySpecialPrices: async (ids: string[]) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/delete-many`, { ids })
    );

    if (success) {
      dispatch?.(specialPriceActions.deleteSpecialPrices({ ids }));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  /**
   * The percentages in force for a zone at one pickup datetime. Add Booking and
   * Add Quotation price trips client-side, so they need this to match what
   * yelo-userback would quote for the same trip.
   *
   * Cached in the slice under `${zone_id}|${pickupIso}`; only the percentages
   * are stored, never a price.
   */
  getApplicableSpecialPrices: async (zone_id: string, time_stamp: string) => {
    const dispatch = getAppDispatch();
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/applicable`, { zone_id, time_stamp })
    );

    if (success) {
      const { specialPrices } = success.data.data;
      const percentByClass = (specialPrices || []).reduce(
        (acc: any, row: any) => {
          acc[String(row.class_id)] = Number(row.increase_percent) || 0;
          return acc;
        },
        {}
      );
      dispatch?.(
        specialPriceActions.setApplicable({
          key: `${zone_id}|${time_stamp}`,
          percentByClass,
        })
      );
    }

    return [success, error];
  },
};

export default SpecialPriceService;
